<mat-card class="py-20" style="width: max-content; max-width: 100%; box-sizing: border-box;">
  <mat-card-header>
    <div class="g-just-flex flex-between w-100 flex-wrap gap-20">
      <div class="left">
        <mat-card-title>Medication Requests</mat-card-title>
        <mat-card-subtitle>Find, search and add current and past medication requests</mat-card-subtitle>
      </div>
      <div class="right gap-10 g-just-flex" style="flex: 1 1 auto; justify-content: flex-end; flex-wrap: wrap;">
        <button mat-stroked-button extended class="secondary-button" (click)="refresh()" [disabled]="loading$ | async">
          <mat-icon [class.g-spin]="loading$ | async">refresh</mat-icon> Refresh
        </button>

        @if (canPrescribeMedications$ | async) {
        <button mat-flat-button extended (click)="encounterService.addMedicationRequest(patientId || '')">
          <mat-icon>add</mat-icon> Add Med. Request
        </button>
        }
      </div>
    </div>
  </mat-card-header>

  <mat-card-content class="my-12">
    <mat-divider></mat-divider>

    <app-table-header [patientsTableFilterArray]="tableFilter"
      [patientsFiltersFormControlObject]="tableFilterFormControlObject" 
      [searchableFields]="searchableFields"
      [selectedSearchField]="selectedSearchField"
      (filterChanged)="onFilterChanged($event)"
      (searchCriteriaChanged)="onSearchCriteriaChanged($event)">
    </app-table-header>

    <div class="table-container" style="position: relative; min-height: 200px;">
      @if (loading$ | async) {
      <div class="loading-shade g-just-flex flex-center">
        <mat-spinner diameter="40"></mat-spinner>
      </div>
      }

      @if ((total$ | async) !== 0) {

      <table mat-table [dataSource]="tableDataSource" class="mat-elevation-z8 demo-table">

        <!-- Group Identifier Column -->
        <ng-container matColumnDef="groupIdentifier">
          <th mat-header-cell *matHeaderCellDef>Requisition / Group</th>
          <td mat-cell *matCellDef="let element; let i = index;"
            style="max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" [ngClass]="{
                  'nob': element.groupIdentifier !== 'N/A',
                  'not': element.groupIdentifier !== 'N/A' && (i === (tableDataSource.data.length - 1) || tableDataSource.data[i+1].groupIdentifier !== element.groupIdentifier)
                }">
            @if (i === 0 || tableDataSource.data[i-1].groupIdentifier !== element.groupIdentifier) {
            <span class="emphasis-text">{{ element.groupIdentifier }}</span>
            }
          </td>
        </ng-container>

        <!-- ID Column -->
        <ng-container matColumnDef="id">
          <th mat-header-cell *matHeaderCellDef>Request ID</th>
          <td mat-cell *matCellDef="let element">
              <span class="text-muted">{{ 'MedicationRequest/' + element.rawResource.id | referenceIdentifier | async }}</span>
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let element">
            <div
              [attr.class]="'small-chips status-' + (statusStyles[element.status]?.['color'] || 'grey') + ' g-just-flex gap-8'">
              <span>{{ element.status }}</span>
              <mat-icon>{{ statusStyles[element.status]?.['icon'] || 'info' }}</mat-icon>
            </div>
          </td>
        </ng-container>

        <!-- Subject Column -->
        <ng-container matColumnDef="subject">
          <th mat-header-cell *matHeaderCellDef>Patient</th>
          <td mat-cell *matCellDef="let element">
            <div class="patient-cell g-just-flex gap-8 flex-center-y">
              <mat-icon color="primary" style="font-size: 18px; width: 18px; height: 18px;">person</mat-icon>
              <span>{{ element.patientName }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Medication Column -->
        <ng-container matColumnDef="medication">
          <th mat-header-cell *matHeaderCellDef>Medication</th>
          <td mat-cell *matCellDef="let element" style="white-space: normal; max-width: 300px; padding: 8px 0;">
            <div class="medication-cell g-just-flex gap-8 flex-center-y justify-content-start py-6;">
              <mat-icon color="accent" style="font-size: 18px; width: 18px; height: 18px;">pill</mat-icon>
              <span class="emphasis-text">{{ element.medicationName }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="action">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let element">
            <button mat-icon-button [matMenuTriggerFor]="menu">
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #menu="matMenu">
              <button mat-menu-item (click)="showRow(element.rawResource)">
                <mat-icon color="primary">visibility</mat-icon>
                <span>View Details</span>
              </button>
              @if (element.dispenseState?.isDispensable) {
                @if (canDispense$ | async) {
                <button mat-menu-item (click)="handleDetailAction('recordDispense', element.rawResource)">
                  <mat-icon color="accent">local_pharmacy</mat-icon>
                  <span>Dispense</span>
                </button>
                }
                @if (canAdminister$ | async) {
                <button mat-menu-item (click)="handleDetailAction('recordAdministration', element.rawResource)">
                  <mat-icon color="primary">vaccines</mat-icon>
                  <span>Administer</span>
                </button>
                }
              }
              @if (isPractitioner$ | async) {
              <button mat-menu-item (click)="handleDetailAction('messageRequester', element.rawResource)">
                <mat-icon color="primary">chat</mat-icon>
                <span>Message Requester</span>
              </button>
              }
            </mat-menu>
          </td>
        </ng-container>

        <!-- Group Actions Column -->
        <ng-container matColumnDef="groupactions">
          <th mat-header-cell *matHeaderCellDef>Group Actions</th>
          <td mat-cell *matCellDef="let element; let i = index;" [ngClass]="{
                  'nob-last': element.groupIdentifier !== 'N/A',
                  'not-last': element.groupIdentifier !== 'N/A' && (i === (tableDataSource.data.length - 1) || tableDataSource.data[i+1].groupIdentifier !== element.groupIdentifier)
                }">
            @if ((i === 0 || tableDataSource.data[i-1].groupIdentifier !== element.groupIdentifier) && (isPractitioner$ | async)) {
            @if (element.groupIdentifier !== 'N/A') {
            <button mat-button [matMenuTriggerFor]="gMenu">
              <mat-icon>settings</mat-icon> Group
            </button>
            <mat-menu #gMenu="matMenu">
              <button mat-menu-item (click)="printMedicationGroup(element.groupIdentifier)">
                <mat-icon>print</mat-icon>
                <span>Print Group</span>
              </button>
              <button mat-menu-item (click)="showPrices(element.groupIdentifier)">
                <mat-icon>attach_money</mat-icon>
                <span>View Pricing</span>
              </button>
            </mat-menu>
            } @else {
            <mat-icon color="disabled">block</mat-icon>
            }
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="tableColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: tableColumns;"></tr>
      </table>

      <mat-paginator [length]="total$ | async" [pageSize]="pageSize" [pageIndex]="paginatorPageIndex"
        (page)="onPageChange($event)" showFirstLastButtons>
      </mat-paginator>
      } @else if (!(loading$ | async)) {
      <app-empty-state [title]="'No Medication Requests Found'"
        [subtitle]="'Refine your search or add a new request for this patient.'" [icon]="'PILLS'">
      </app-empty-state>
      }
    </div>
  </mat-card-content>
</mat-card>